import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2, 
  Download,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Package,
  DollarSign,
  User,
  CreditCard,
  FileText,
  X,
  ExternalLink,
  CheckSquare,
  Square,
  Zap,
  Loader2,
  Star,
  Percent
} from 'lucide-react';
import { format } from 'date-fns';
import { orderAPI, pdfGenerationAPI } from '../../services/api';
import { getImageUrl, getApiBaseUrl } from '../../utils/apiUtils';
import { 
  mapPaymentMethodToDisplay, 
  mapPaymentStatusToDisplay, 
  mapOrderStatusToDisplay,
  getStatusBadge,
  getStatusColor,
  getStatusIcon
} from '../../utils/paymentUtils';

const OrdersManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [stats, setStats] = useState({});

  // Bulk actions state
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectAllOrders, setSelectAllOrders] = useState(false);
  const [allOrderIds, setAllOrderIds] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Pagination and filters
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    payment_status: '',
    payment_method: '',
    has_coupon: '',
    date_from: '',
    date_to: '',
    sort_by: 'created_at',
    sort_order: 'DESC'
  });

  // Fetch orders
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });

      const response = await fetch(`${getApiBaseUrl()}/orders?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setOrders(data.data.orders);
        setPagination(data.data.pagination);
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError('Failed to fetch orders');
      console.error('Fetch orders error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch order statistics
  const fetchStats = async () => {
    try {
      const params = new URLSearchParams({
        date_from: filters.date_from,
        date_to: filters.date_to
      });

      const response = await fetch(`${getApiBaseUrl()}/orders/stats/summary?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Fetch stats error:', error);
    }
  };

  // Fetch all order IDs for bulk selection
  const fetchAllOrderIds = async () => {
    try {
      const params = new URLSearchParams({
        ...filters,
        limit: 10000, // Large limit to get all orders
        page: 1
      });

      const response = await fetch(`${getApiBaseUrl()}/orders?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      const data = await response.json();

      if (data.success) {
        const ids = data.data.orders.map(order => order.id);
        setAllOrderIds(ids);
      }
    } catch (error) {
      console.error('Fetch all order IDs error:', error);
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchStats();
    fetchAllOrderIds();
  }, [pagination.page, filters]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchOrders();
  };

  // Export orders to CSV
  const exportOrders = () => {
    try {
      const headers = [
        'Order Number',
        'Customer Name',
        'Customer Email',
        'Customer Phone',
        'Total Amount',
        'Payment Status',
        'Order Status',
        'Payment Method',
        'Payment Option',
        'Original Total',
        'Advance Amount',
        'Remaining Amount',
        'Order Date'
      ];

      const csvData = [
        headers.join(','),
        ...orders.map(order => [
          order.order_number,
          order.customer_name,
          order.customer_email,
          order.customer_phone || '',
          order.total_amount,
          order.payment_status,
          order.order_status,
          order.payment_method || '',
          order.payment_option || 'full',
          order.original_total_amount || order.total_amount,
          order.advance_amount || '',
          order.remaining_amount || '',
          format(new Date(order.created_at), 'yyyy-MM-dd HH:mm:ss')
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `orders_${format(new Date(), 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Export error:', error);
      setError('Failed to export orders');
    }
  };

  // Order PDF download and print handlers
  const handleDownloadOrderPDF = async (order) => {
    try {
      console.log('🔍 Downloading order PDF for order:', order.id);
      
      const response = await pdfGenerationAPI.generateOrderPDF(order.id);
      
      if (response?.data) {
        const blob = response.data;
        
        if (blob.size === 0) {
          throw new Error('PDF blob is empty');
        }

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `order-${order.order_number}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log('✅ Order PDF downloaded successfully');
      } else {
        throw new Error('No PDF data received');
      }
    } catch (error) {
      console.error('❌ Error downloading order PDF:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to download order PDF. ';
      if (error.response?.status === 404) {
        errorMessage += 'Order not found.';
      } else if (error.response?.status === 401) {
        errorMessage += 'Please log in again.';
      } else if (error.response?.status === 500) {
        errorMessage += 'Server error. Please try again later.';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage += 'Network error. Please check your connection.';
      } else {
        errorMessage += error.message || 'Unknown error occurred.';
      }
      
      alert(errorMessage);
    }
  };

  const handlePrintOrder = async (order) => {
    try {
      console.log('🔍 Printing order for order:', order.id);
      
      const response = await pdfGenerationAPI.generateOrderPDF(order.id);
      
      if (response?.data) {
        const blob = response.data;
        
        if (blob.size === 0) {
          throw new Error('PDF blob is empty');
        }

        // Create blob URL and open in new window for printing
        const url = window.URL.createObjectURL(blob);
        const printWindow = window.open(url, '_blank');
        
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
            // Clean up the blob URL after printing
            setTimeout(() => {
              window.URL.revokeObjectURL(url);
            }, 1000);
          };
        } else {
          throw new Error('Popup blocked. Please allow popups for this site.');
        }
        
        console.log('✅ Order PDF opened for printing');
      } else {
        throw new Error('No PDF data received');
      }
    } catch (error) {
      console.error('❌ Error printing order:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to print order. ';
      if (error.response?.status === 404) {
        errorMessage += 'Order not found.';
      } else if (error.response?.status === 401) {
        errorMessage += 'Please log in again.';
      } else if (error.response?.status === 500) {
        errorMessage += 'Server error. Please try again later.';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage += 'Network error. Please check your connection.';
      } else if (error.message.includes('Popup blocked')) {
        errorMessage += 'Please allow popups for this site.';
      } else {
        errorMessage += error.message || 'Unknown error occurred.';
      }
      
      alert(errorMessage);
    }
  };

  // View order details
  const viewOrder = async (orderId) => {
    try {
      console.log('🔍 Viewing order:', orderId);
      const response = await fetch(`${getApiBaseUrl()}/orders/id/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      const data = await response.json();
      console.log('🔍 Order data response:', data);

      if (data.success) {
        setSelectedOrder(data.data.order);
        setShowOrderModal(true);
        console.log('🔍 Order modal opened successfully');
      } else {
        console.error('View order error:', data.message);
      }
    } catch (error) {
      console.error('View order error:', error);
    }
  };

  // Update order
  const updateOrder = async (orderId, updateData) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/orders/${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (data.success) {
        fetchOrders();
        setShowEditModal(false);
        setSelectedOrder(null);
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError('Failed to update order');
      console.error('Update order error:', error);
    }
  };

  // Delete order
  const deleteOrder = async (orderId) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/orders/${orderId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      const data = await response.json();

      if (data.success) {
        fetchOrders();
        setDeleteConfirm(null);
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError('Failed to delete order');
      console.error('Delete order error:', error);
    }
  };

  // Get status icon and color
  const getStatusIconComponent = (status) => {
    const iconName = getStatusIcon(status, 'order');
    const IconComponent = {
      'Clock': Clock,
      'CheckCircle': CheckCircle,
      'XCircle': XCircle,
      'AlertCircle': AlertCircle,
      'Package': Package
    }[iconName] || Clock;
    
    return <IconComponent className={`h-4 w-4 ${getStatusColor(status, 'order')}`} />;
  };

  // Get payment status icon and color
  const getPaymentStatusIconComponent = (status) => {
    const iconName = getStatusIcon(status, 'payment');
    const IconComponent = {
      'Clock': Clock,
      'CheckCircle': CheckCircle,
      'XCircle': XCircle,
      'AlertCircle': AlertCircle,
      'Package': Package
    }[iconName] || Clock;
    
    return <IconComponent className={`h-4 w-4 ${getStatusColor(status, 'payment')}`} />;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  // Bulk action functions
  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        const newSelection = prev.filter(id => id !== orderId);
        // If we're deselecting from "all orders" selection, switch to individual selection
        if (selectAllOrders && newSelection.length < allOrderIds.length) {
          setSelectAllOrders(false);
        }
        return newSelection;
      } else {
        const newSelection = [...prev, orderId];
        // If we're selecting all orders individually, switch to "all orders" selection
        if (!selectAllOrders && newSelection.length === allOrderIds.length) {
          setSelectAllOrders(true);
        }
        return newSelection;
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedOrders([]);
      setSelectAll(false);
      setSelectAllOrders(false);
    } else {
      setSelectedOrders(orders.map(order => order.id));
      setSelectAll(true);
      setSelectAllOrders(false);
    }
  };

  const handleSelectAllOrders = () => {
    if (selectAllOrders) {
      setSelectedOrders([]);
      setSelectAllOrders(false);
      setSelectAll(false);
    } else {
      setSelectedOrders(allOrderIds);
      setSelectAllOrders(true);
      setSelectAll(false);
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    if (!selectedOrders.length) return;
    
    if (!window.confirm(`Are you sure you want to update ${selectedOrders.length} order(s) status to ${status}?`)) {
      return;
    }

    try {
      setBulkActionLoading(true);
      const response = await orderAPI.bulkUpdateStatus(selectedOrders, status);
      
      if (response?.data?.success) {
        await fetchOrders();
        setSelectedOrders([]);
        setSelectAll(false);
        setSelectAllOrders(false);
        setShowBulkActions(false);
      } else {
        setError('Failed to update order status');
      }
    } catch (err) {
      console.error('Error updating order status:', err);
      setError(`Error updating order status: ${err.message}`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkPaymentStatusUpdate = async (paymentStatus) => {
    if (!selectedOrders.length) return;
    
    if (!window.confirm(`Are you sure you want to update ${selectedOrders.length} order(s) payment status to ${paymentStatus}?`)) {
      return;
    }

    try {
      setBulkActionLoading(true);
      const response = await orderAPI.bulkUpdatePaymentStatus(selectedOrders, paymentStatus);
      
      if (response?.data?.success) {
        await fetchOrders();
        setSelectedOrders([]);
        setSelectAll(false);
        setSelectAllOrders(false);
        setShowBulkActions(false);
      } else {
        setError('Failed to update payment status');
      }
    } catch (err) {
      console.error('Error updating payment status:', err);
      setError(`Error updating payment status: ${err.message}`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedOrders.length) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedOrders.length} order(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      setBulkActionLoading(true);
      const response = await orderAPI.bulkDelete(selectedOrders);
      
      if (response?.data?.success) {
        await fetchOrders();
        setSelectedOrders([]);
        setSelectAll(false);
        setSelectAllOrders(false);
        setShowBulkActions(false);
      } else {
        setError('Failed to delete orders');
      }
    } catch (err) {
      console.error('Error deleting orders:', err);
      setError(`Error deleting orders: ${err.message}`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Update showBulkActions when selectedOrders changes
  useEffect(() => {
    setShowBulkActions(selectedOrders.length > 0);
  }, [selectedOrders]);

  if (loading && orders.length === 0) {
    return (
      <div className="p-4 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <span className="ml-2 text-gray-600">Loading orders...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <AlertCircle className="w-8 h-8 text-red-600" />
          <span className="ml-2 text-red-600">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <Package className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
              Orders Management
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
              Manage customer orders and track payments
            </p>
          </div>
          <button
            onClick={exportOrders}
            className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center touch-manipulation"
          >
            <Download className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Export Orders</span>
            <span className="sm:hidden">Export</span>
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-4 sm:p-6 mb-4 sm:mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-1.5 sm:p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <Package className="h-4 w-4 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-2 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Total Orders</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                {stats.summary?.total_orders || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-1.5 sm:p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
              <DollarSign className="h-4 w-4 sm:h-6 sm:w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-2 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Total Revenue</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(stats.summary?.total_revenue || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-1.5 sm:p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
              <CheckCircle className="h-4 w-4 sm:h-6 sm:w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="ml-2 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Completed</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                {stats.summary?.completed_orders || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-1.5 sm:p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
              <Clock className="h-4 w-4 sm:h-6 sm:w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="ml-2 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Pending</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                {stats.summary?.pending_orders || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm mb-4 sm:mb-6">
        <form onSubmit={handleSearch} className="space-y-3 sm:space-y-4">
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center space-y-3 lg:space-y-0 lg:space-x-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search orders..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            {/* Payment Status Filter */}
            <select
              value={filters.payment_status}
              onChange={(e) => handleFilterChange('payment_status', e.target.value)}
              className="px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
            >
              <option value="">All Payment Status</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>

            {/* Payment Method Filter */}
            <select
              value={filters.payment_method}
              onChange={(e) => handleFilterChange('payment_method', e.target.value)}
              className="px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
            >
              <option value="">All Payment Methods</option>
              <option value="razorpay">Razorpay</option>
              <option value="cashfree">Cashfree</option>
              <option value="offline">Offline</option>
            </select>

            {/* Coupon Filter */}
            <select
              value={filters.has_coupon || ''}
              onChange={(e) => handleFilterChange('has_coupon', e.target.value)}
              className="px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
            >
              <option value="">All Orders</option>
              <option value="with_coupon">With Coupon</option>
              <option value="without_coupon">Without Coupon</option>
            </select>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            {/* Date Range */}
            <div className="flex-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>

            <div className="flex-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>

            <div className="flex items-end">
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-6 py-2 rounded-lg flex items-center touch-manipulation"
              >
                <Search className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Search</span>
                <span className="sm:hidden">Search</span>
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Bulk Actions */}
      {showBulkActions && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3 sm:p-4 rounded-lg mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <span className="text-xs sm:text-sm font-medium text-blue-900 dark:text-blue-100">
                {selectedOrders.length} order(s) selected
                {selectAllOrders && ` (All ${allOrderIds.length} orders)`}
              </span>
              
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => handleBulkStatusUpdate('pending')}
                  disabled={bulkActionLoading}
                  className="px-2 sm:px-3 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center touch-manipulation"
                >
                  <Zap className="w-3 h-3 mr-1" />
                  <span className="hidden sm:inline">Set Pending</span>
                  <span className="sm:hidden">Pending</span>
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('processing')}
                  disabled={bulkActionLoading}
                  className="px-2 sm:px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center touch-manipulation"
                >
                  <Zap className="w-3 h-3 mr-1" />
                  <span className="hidden sm:inline">Set Processing</span>
                  <span className="sm:hidden">Processing</span>
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('completed')}
                  disabled={bulkActionLoading}
                  className="px-2 sm:px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center touch-manipulation"
                >
                  <Zap className="w-3 h-3 mr-1" />
                  <span className="hidden sm:inline">Set Completed</span>
                  <span className="sm:hidden">Completed</span>
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('cancelled')}
                  disabled={bulkActionLoading}
                  className="px-2 sm:px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center touch-manipulation"
                >
                  <Zap className="w-3 h-3 mr-1" />
                  <span className="hidden sm:inline">Set Cancelled</span>
                  <span className="sm:hidden">Cancelled</span>
                </button>
              </div>
              
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => handleBulkPaymentStatusUpdate('pending')}
                  disabled={bulkActionLoading}
                  className="px-2 sm:px-3 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center touch-manipulation"
                >
                  <CreditCard className="w-3 h-3 mr-1" />
                  <span className="hidden sm:inline">Payment Pending</span>
                  <span className="sm:hidden">Pending</span>
                </button>
                
                <button
                  onClick={() => handleBulkPaymentStatusUpdate('completed')}
                  disabled={bulkActionLoading}
                  className="px-2 sm:px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center touch-manipulation"
                >
                  <CreditCard className="w-3 h-3 mr-1" />
                  <span className="hidden sm:inline">Payment Completed</span>
                  <span className="sm:hidden">Completed</span>
                </button>
                
                <button
                  onClick={() => handleBulkPaymentStatusUpdate('failed')}
                  disabled={bulkActionLoading}
                  className="px-2 sm:px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center touch-manipulation"
                >
                  <CreditCard className="w-3 h-3 mr-1" />
                  <span className="hidden sm:inline">Payment Failed</span>
                  <span className="sm:hidden">Failed</span>
                </button>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={handleBulkDelete}
                disabled={bulkActionLoading}
                className="px-2 sm:px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center touch-manipulation"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                <span className="hidden sm:inline">Delete Selected</span>
                <span className="sm:hidden">Delete</span>
              </button>
              
              <button
                onClick={() => {
                  setSelectedOrders([]);
                  setSelectAll(false);
                  setSelectAllOrders(false);
                  setShowBulkActions(false);
                }}
                className="px-2 sm:px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 touch-manipulation"
              >
                <X className="w-3 h-3 mr-1" />
                <span className="hidden sm:inline">Clear Selection</span>
                <span className="sm:hidden">Clear</span>
              </button>
            </div>
          </div>
          
          {bulkActionLoading && (
            <div className="mt-3 flex items-center text-blue-700 dark:text-blue-300">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              <span className="text-sm">Processing bulk action...</span>
            </div>
          )}
        </div>
      )}

      {/* Select All Orders Button */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleSelectAllOrders}
            className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center space-x-2 transition-colors ${
              selectAllOrders 
                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            {selectAllOrders ? (
              <CheckSquare className="h-4 w-4" />
            ) : (
              <Square className="h-4 w-4" />
            )}
            <span>
              {selectAllOrders ? 'All Orders Selected' : `Select All Orders (${allOrderIds.length})`}
            </span>
          </button>
          
          {selectAllOrders && (
            <span className="text-sm text-gray-600 dark:text-gray-400">
              All {allOrderIds.length} orders across all pages are selected
            </span>
          )}
        </div>
        
        {selectedOrders.length > 0 && (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {selectedOrders.length} order(s) selected
          </div>
        )}
      </div>

      {/* Orders Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  <div className="flex items-center">
                    <button
                      onClick={handleSelectAll}
                      className="mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      {selectAll ? (
                        <CheckSquare className="h-4 w-4 text-blue-600" />
                      ) : (
                        <Square className="h-4 w-4" />
                      )}
                    </button>
                    Order
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Payment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {orders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    No orders found
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <button
                          onClick={() => handleSelectOrder(order.id)}
                          className="mr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          {selectedOrders.includes(order.id) ? (
                            <CheckSquare className="h-4 w-4 text-blue-600" />
                          ) : (
                            <Square className="h-4 w-4" />
                          )}
                        </button>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {order.order_number}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {order.items?.length || 0} items
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {order.customer_name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {order.customer_email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatCurrency(order.total_amount)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIconComponent(order.order_status)}
                        <span className="ml-2 text-sm text-gray-900 dark:text-white">
                          {mapOrderStatusToDisplay(order.order_status)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getPaymentStatusIconComponent(order.payment_status)}
                        <span className="ml-2 text-sm text-gray-900 dark:text-white">
                          {mapPaymentStatusToDisplay(order.payment_status)}
                        </span>
                      </div>
                      {order.payment_method && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {mapPaymentMethodToDisplay(order.payment_method)}
                        </div>
                      )}
                      {/* Coupon Badge */}
                      {(() => {
                        const couponCode = order.payment_option_details?.appliedCoupon?.code || 
                                         order.payment_option_details?.coupon_code ||
                                         (order.applied_coupon ? (() => {
                                           try {
                                             const parsed = typeof order.applied_coupon === 'string' ? JSON.parse(order.applied_coupon) : order.applied_coupon;
                                             return parsed.code;
                                           } catch (e) {
                                             return null;
                                           }
                                         })() : null) ||
                                         null;
                        
                        if (couponCode) {
                          return (
                            <div className="flex items-center text-xs text-green-600 dark:text-green-400 mt-1">
                              <Percent className="h-3 w-3 mr-1" />
                              {couponCode}
                            </div>
                          );
                        }
                        return null;
                      })()}
                      {/* Payment Option Badge */}
                      {order.payment_option && order.payment_option !== 'full' && (
                        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-1 ${
                          order.payment_option === 'advance' 
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' 
                            : order.payment_option === 'emi'
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                            : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        }`}>
                          {order.payment_option === 'advance' ? 'Split' : 
                           order.payment_option === 'emi' ? 'EMI' : 'Full'}
                        </div>
                      )}
                      {/* Split Payment Badge */}
                      {order.payment_option_details?.is_split_payment_order && (
                        <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-1 bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                          Split Payment - {order.payment_option_details.split_payment_text || 'Remaining'}
                        </div>
                      )}
                      {order.payment_proof && (
                        <div className="flex items-center text-xs text-blue-600 dark:text-blue-400 mt-1">
                          <FileText className="h-3 w-3 mr-1" />
                          Proof uploaded
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {format(new Date(order.created_at), 'MMM dd, yyyy')}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {format(new Date(order.created_at), 'HH:mm')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => viewOrder(order.id)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowEditModal(true);
                          }}
                          className="text-green-600 hover:text-green-900 p-1 rounded"
                          title="Edit Order"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(order)}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Delete Order"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Showing{' '}
                  <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span>
                  {' '}to{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>
                  {' '}of{' '}
                  <span className="font-medium">{pagination.total}</span>
                  {' '}results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => setPagination(prev => ({ ...prev, page }))}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === pagination.page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => {
            setShowOrderModal(false);
            setSelectedOrder(null);
          }}
          onDownloadPDF={handleDownloadOrderPDF}
          onPrintPDF={handlePrintOrder}
        />
      )}

      {/* Edit Order Modal */}
      {showEditModal && selectedOrder && createPortal(
        <EditOrderModal
          order={selectedOrder}
          onClose={() => {
            setShowEditModal(false);
            setSelectedOrder(null);
          }}
          onUpdate={updateOrder}
        />
      , document.body)}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <DeleteConfirmModal
          order={deleteConfirm}
          onClose={() => setDeleteConfirm(null)}
          onDelete={deleteOrder}
        />
      )}
    </div>
  );
};

// Order Details Modal
const OrderDetailsModal = ({ order, onClose, onDownloadPDF, onPrintPDF }) => {
  if (!order) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Order Details - {order.order_number}
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onDownloadPDF(order)}
                className="p-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                title="Download Order PDF"
              >
                <Download className="h-5 w-5" />
              </button>
              <button
                onClick={() => onPrintPDF(order)}
                className="p-2 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                title="Print Order"
              >
                <FileText className="h-5 w-5" />
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-6">
          {/* Order Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Order Information</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Order Number:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{order.order_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Order Date:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {format(new Date(order.created_at), 'MMM dd, yyyy HH:mm')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Order Status:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{mapOrderStatusToDisplay(order.order_status)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Total Amount:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {new Intl.NumberFormat('en-IN', {
                      style: 'currency',
                      currency: 'INR'
                    }).format(order.total_amount)}
                  </span>
                </div>
                
                {/* Split Payment Parent Order Number and Parent Split Payment ID */}
                {order.payment_option_details?.is_split_payment_order && (
                  <>
                    {order.payment_option_details.original_order_number && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Split Payment Parent Order Number:</span>
                        <span className="font-medium text-blue-600 dark:text-blue-400">
                          #{order.payment_option_details.original_order_number}
                        </span>
                      </div>
                    )}
                    {order.payment_option_details.parent_split_payment_id && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Parent Split Payment ID:</span>
                        <span className="font-medium text-blue-600 dark:text-blue-400">
                          #{order.payment_option_details.parent_split_payment_id}
                        </span>
                      </div>
                    )}
                  </>
                )}
                
                {/* Coupon Code in Order Summary */}
                {(() => {
                  // Check for coupon in multiple locations for split payments
                  const couponCode = order.payment_option_details?.appliedCoupon?.code || 
                                   order.payment_option_details?.coupon_code ||
                                   (order.applied_coupon ? (() => {
                                     try {
                                       const parsed = typeof order.applied_coupon === 'string' ? JSON.parse(order.applied_coupon) : order.applied_coupon;
                                       return parsed.code;
                                     } catch (e) {
                                       return null;
                                     }
                                   })() : null) ||
                                   null;
                  
                  if (couponCode) {
                    const discountPercentage = order.payment_option_details?.appliedCoupon?.discount_percentage || 
                                             order.payment_option_details?.discount_percentage ||
                                             (order.applied_coupon ? (() => {
                                               try {
                                                 const parsed = typeof order.applied_coupon === 'string' ? JSON.parse(order.applied_coupon) : order.applied_coupon;
                                                 return parsed.discount_percentage;
                                               } catch (e) {
                                                 return 0;
                                               }
                                             })() : 0) ||
                                             0;
                    
                    const calculatedDiscountAmount = order.payment_option_details?.appliedCoupon?.calculated_discount_amount ||
                                                   (order.applied_coupon ? (() => {
                                                     try {
                                                       const parsed = typeof order.applied_coupon === 'string' ? JSON.parse(order.applied_coupon) : order.applied_coupon;
                                                       return parsed.calculated_discount_amount;
                                                     } catch (e) {
                                                       return 0;
                                                     }
                                                   })() : 0) ||
                                                   0;
                    
                    return (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 dark:text-gray-400 flex items-center">
                          <Percent className="w-4 h-4 mr-1" />
                          Coupon Applied:
                        </span>
                        <div className="text-right">
                          <span className="font-medium text-green-600 dark:text-green-400">
                            {couponCode}
                          </span>
                          {(discountPercentage > 0 || calculatedDiscountAmount > 0) && (
                            <div className="text-xs text-green-600 dark:text-green-400">
                              {(() => {
                                if (calculatedDiscountAmount > 0) {
                                  const inrText = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(calculatedDiscountAmount);
                                  return `-${inrText}`;
                                } else if (discountPercentage > 0) {
                                  const originalTotal = Number(order.original_total_amount || 0);
                                  const totalAmount = Number(order.total_amount || 0);
                                  const discountInr = originalTotal && originalTotal > totalAmount ? (originalTotal - totalAmount) : 0;
                                  const percentText = `-${discountPercentage}%`;
                                  if (discountInr > 0) {
                                    const inrText = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(discountInr);
                                    return `${percentText} (${inrText})`;
                                  }
                                  return percentText;
                                }
                                return '';
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>

            {/* Order Breakdown - Enhanced for both After Project Completion and After Demo Completion */}
            {(order.payment_option_details?.is_split_payment_order || order.payment_option === 'advance') && (() => {
              const couponCode = order.payment_option_details?.appliedCoupon?.code || 
                               order.payment_option_details?.coupon_code ||
                               (order.applied_coupon ? (() => {
                                 try {
                                   const parsed = typeof order.applied_coupon === 'string' ? JSON.parse(order.applied_coupon) : order.applied_coupon;
                                   return parsed.code;
                                 } catch (e) {
                                   return null;
                                 }
                               })() : null) ||
                               null;
              
              const calculatedDiscountAmount = order.payment_option_details?.appliedCoupon?.calculated_discount_amount ||
                                             (order.applied_coupon ? (() => {
                                               try {
                                                 const parsed = typeof order.applied_coupon === 'string' ? JSON.parse(order.applied_coupon) : order.applied_coupon;
                                                 return parsed.calculated_discount_amount;
                                               } catch (e) {
                                                 return 0;
                                               }
                                             })() : 0) ||
                                             0;
              
              // Determine if this is a split payment order and get the type
              const isSplitPaymentOrder = order.payment_option_details?.is_split_payment_order;
              const splitPaymentType = order.payment_option_details?.split_payment_type;
              const splitPaymentText = order.payment_option_details?.split_payment_text;
              
              // Show order breakdown for both After Project Completion and After Demo Completion orders
              if (isSplitPaymentOrder || order.payment_option === 'advance') {
                const originalAmount = Number(order.total_amount || 0) + calculatedDiscountAmount;
                
                return (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Order Breakdown</h3>
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <div className="space-y-2">
                        {/* Split Payment Type Information */}
                        {(splitPaymentType || splitPaymentText) && (
                          <div className="flex justify-between items-center pb-2 border-b border-blue-200 dark:border-blue-700">
                            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Payment Type:</span>
                            <span className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                              {splitPaymentText || 
                               (splitPaymentType === 'after_demo' ? 'After Demo Completion' : 
                                splitPaymentType === 'after_project' ? 'After Project Completion' : 
                                'Split Payment')}
                            </span>
                          </div>
                        )}
                        
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">Order Subtotal:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR'
                            }).format(originalAmount)}
                          </span>
                        </div>
                        
                        {couponCode && calculatedDiscountAmount > 0 && (
                          <>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 dark:text-gray-400">Coupon Code:</span>
                              <span className="font-medium text-green-600 dark:text-green-400">
                                {couponCode}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 dark:text-gray-400">Coupon Discount:</span>
                              <span className="font-medium text-green-600 dark:text-green-400">
                                -{new Intl.NumberFormat('en-IN', {
                                  style: 'currency',
                                  currency: 'INR'
                                }).format(calculatedDiscountAmount)}
                              </span>
                            </div>
                          </>
                        )}
                        
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
                          <div className="flex justify-between items-center">
                            <span className="text-lg font-semibold text-gray-900 dark:text-white">Final Amount:</span>
                            <span className="text-lg font-bold text-gray-900 dark:text-white">
                              {new Intl.NumberFormat('en-IN', {
                                style: 'currency',
                                currency: 'INR'
                              }).format(order.total_amount || 0)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Payment Information</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Payment Status:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{mapPaymentStatusToDisplay(order.payment_status)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Payment Method:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{mapPaymentMethodToDisplay(order.payment_method)}</span>
                </div>
                
                {/* Payment Type/Option */}
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Payment Type:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {order.payment_option_details?.is_split_payment_order ? 'Split Payment' :
                     order.payment_option === 'full' ? 'Full Payment' :
                     order.payment_option === 'advance' ? 'Split Payment' :
                     order.payment_option === 'emi' ? 'EMI Payment' :
                     order.payment_option || 'Standard Payment'}
                  </span>
                </div>
                
                {/* Payment Option Details */}
                {order.payment_option_details && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Payment Option Details:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {order.payment_option_details?.is_split_payment_order ? 
                        `Split Payment - ${order.payment_option_details.split_payment_text || 'Remaining Amount'}` :
                       order.payment_option_details?.selectedPaymentOption === 'full' ? 'Full Payment' :
                       order.payment_option_details?.selectedPaymentOption === 'advance' ? 'Split Payment' :
                       order.payment_option_details?.selectedPaymentOption === 'emi' ? 'EMI Payment' :
                       order.payment_option_details?.selectedPaymentOption || 'Standard Payment'}
                    </span>
                  </div>
                )}
                
                {/* Payment Option Details */}
                {order.payment_option && order.payment_option !== 'full' && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Payment Option:</span>
                      <span className={`font-medium ${
                        order.payment_option === 'advance' 
                          ? 'text-blue-600 dark:text-blue-400' 
                          : order.payment_option === 'emi'
                          ? 'text-purple-600 dark:text-purple-400'
                          : 'text-green-600 dark:text-green-400'
                      }`}>
                        {order.payment_option === 'advance' ? 'Split Payment' : 
                         order.payment_option === 'emi' ? 'EMI Payment' : 'Full Payment'}
                      </span>
                    </div>
                    {order.original_total_amount && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Original Total:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR'
                          }).format(order.original_total_amount)}
                        </span>
                      </div>
                    )}
                    {order.advance_amount && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Advance Amount:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR'
                          }).format(order.advance_amount)}
                        </span>
                      </div>
                    )}
                    {order.remaining_amount && order.remaining_amount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Remaining Amount:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR'
                          }).format(order.remaining_amount)}
                        </span>
                      </div>
                    )}
                    
                    {/* EMI Details */}
                    {order.payment_option === 'emi' && (
                      <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                        <h4 className="text-sm font-medium text-purple-800 dark:text-purple-200 mb-2">EMI Details</h4>
                        <div className="space-y-1 text-sm">
                          {order.payment_option_details?.emi_details ? (
                            <>
                              <div className="flex justify-between">
                                <span className="text-purple-700 dark:text-purple-300">EMI Tenure:</span>
                                <span className="text-purple-900 dark:text-purple-100">
                                  {order.payment_option_details.emi_details.selectedEmiTenure} months
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-purple-700 dark:text-purple-300">Monthly EMI:</span>
                                <span className="text-purple-900 dark:text-purple-100">
                                  {new Intl.NumberFormat('en-IN', {
                                    style: 'currency',
                                    currency: 'INR'
                                  }).format(order.payment_option_details.emi_details.emiAmount || 0)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-purple-700 dark:text-purple-300">EMI Principal:</span>
                                <span className="text-purple-900 dark:text-purple-100">
                                  {new Intl.NumberFormat('en-IN', {
                                    style: 'currency',
                                    currency: 'INR'
                                  }).format(order.payment_option_details.emi_details.emiPrincipal || 0)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-purple-700 dark:text-purple-300">Processing Fee:</span>
                                <span className="text-purple-900 dark:text-purple-100">
                                  {new Intl.NumberFormat('en-IN', {
                                    style: 'currency',
                                    currency: 'INR'
                                  }).format(order.payment_option_details?.paymentOptionsConfig?.emi_processing_fee || 0)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-purple-700 dark:text-purple-300">Interest Rate:</span>
                                <span className="text-purple-900 dark:text-purple-100">
                                  {order.payment_option_details?.paymentOptionsConfig?.emi_interest_rate || 0}% p.a.
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-purple-700 dark:text-purple-300">Total EMI Amount:</span>
                                <span className="text-purple-900 dark:text-purple-100">
                                  {new Intl.NumberFormat('en-IN', {
                                    style: 'currency',
                                    currency: 'INR'
                                  }).format(order.payment_option_details.emi_details.totalEmiAmount || 0)}
                                </span>
                              </div>
                            </>
                          ) : (
                            <div className="text-purple-700 dark:text-purple-300">
                              EMI details not available
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Split Payment Details */}
                    {order.payment_option_details?.is_split_payment_order && (
                      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">Split Payment Details</h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-blue-700 dark:text-blue-300">Split Payment Type:</span>
                            <span className="text-blue-900 dark:text-blue-100">
                              {order.payment_option_details.split_payment_text || 'Split Payment'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-blue-700 dark:text-blue-300">Parent Order:</span>
                            <span className="text-blue-900 dark:text-blue-100">
                              #{order.payment_option_details.original_order_number}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-blue-700 dark:text-blue-300">Original Total Amount:</span>
                            <span className="text-blue-900 dark:text-blue-100">
                              {new Intl.NumberFormat('en-IN', {
                                style: 'currency',
                                currency: 'INR'
                              }).format(order.payment_option_details.original_total_amount || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-blue-700 dark:text-blue-300">Previously Paid:</span>
                            <span className="text-blue-900 dark:text-blue-100">
                              {new Intl.NumberFormat('en-IN', {
                                style: 'currency',
                                currency: 'INR'
                              }).format((() => {
                                // Calculate previously paid: Advance Amount + Split Paid Amount (from parent split payment)
                                const pod = order.payment_option_details || {};
                                
                                // For now, use the stored paid_amount, but this should be calculated dynamically
                                // TODO: Implement the same calculation logic as UserDashboard
                                return pod.paid_amount || 0;
                              })())}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-blue-700 dark:text-blue-300">Remaining Amount:</span>
                            <span className="text-blue-900 dark:text-blue-100">
                              {new Intl.NumberFormat('en-IN', {
                                style: 'currency',
                                currency: 'INR'
                              }).format(order.payment_option_details.remaining_amount || 0)}
                            </span>
                          </div>
                          {order.payment_option_details.parent_split_payment_id && (
                            <div className="flex justify-between">
                              <span className="text-blue-700 dark:text-blue-300">Parent Split Payment ID:</span>
                              <span className="text-blue-900 dark:text-blue-100">
                                #{order.payment_option_details.parent_split_payment_id}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {order.transaction_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Transaction ID:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{order.transaction_id}</span>
                  </div>
                )}

              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Customer Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{order.customer_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{order.customer_email}</p>
              </div>
              {order.customer_phone && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{order.customer_phone}</p>
                </div>
              )}
              {order.customer_whatsapp && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">WhatsApp</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{order.customer_whatsapp}</p>
                </div>
              )}
              {order.customer_address && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Address</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{order.customer_address}</p>
                </div>
              )}
            </div>
            



          </div>

          {/* Order Items */}
          {order.items && order.items.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Order Items</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Item
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {order.items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            {/* Product Image */}
                            {item.product_slug ? (
                              <Link 
                                to={`/product/${item.product_slug}`}
                                className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer"
                              >
                                {item.product_image ? (
                                  <img 
                                    src={getImageUrl(item.product_image)} 
                                    alt={item.name || item.product_name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div className={`w-full h-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center ${item.product_image ? 'hidden' : ''}`}>
                                  <Package className="w-6 h-6 text-gray-400" />
                                </div>
                              </Link>
                            ) : (
                              <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                                {item.product_image ? (
                                  <img 
                                    src={getImageUrl(item.product_image)} 
                                    alt={item.name || item.product_name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div className={`w-full h-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center ${item.product_image ? 'hidden' : ''}`}>
                                  <Package className="w-6 h-6 text-gray-400" />
                                </div>
                              </div>
                            )}
                            <div>
                              {item.product_slug ? (
                                <Link 
                                  to={`/product/${item.product_slug}`}
                                  className="text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer"
                                >
                                  {item.name || item.product_name}
                                </Link>
                              ) : (
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {item.name || item.product_name}
                                </div>
                              )}
                              {item.description && (
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {item.description}
                                </div>
                              )}
                              
                              {/* License Information */}
                              {item.selected_license && (
                                <div className="mt-2 space-y-1">
                                  <div className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                    License: {item.selected_license === 'extended' ? 'Extended License' : 'Regular License'}
                                  </div>
                                  {item.license_details && (
                                    <div className="text-xs text-gray-600 dark:text-gray-400">
                                      {item.selected_license === 'extended' && item.license_details.extended_license_price && (
                                        <span>Price: ₹{item.license_details.extended_license_price}</span>
                                      )}
                                      {item.selected_license === 'regular' && item.license_details.regular_license_price && (
                                        <span>Price: ₹{item.license_details.regular_license_price}</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Extended Support Information */}
                              {item.extended_support && (
                                <div className="mt-2">
                                  <div className="text-xs font-medium text-green-600 dark:text-green-400">
                                    ✓ Extended Support Included
                                  </div>
                                  {item.support_details && item.support_details.extended_support_price && (
                                    <div className="text-xs text-gray-600 dark:text-gray-400">
                                      Support Price: ₹{item.support_details.extended_support_price}
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Addons Information */}
                              {item.selected_addons && (
                                <div className="mt-2">
                                  <div className="text-xs font-medium text-purple-600 dark:text-purple-400">
                                    Addons: {Array.isArray(item.selected_addons) ? item.selected_addons.length : 1} selected
                                  </div>
                                  <div className="text-xs text-gray-600 dark:text-gray-400">
                                    {Array.isArray(item.selected_addons) ? (
                                      item.selected_addons.map((addon, idx) => (
                                        <span key={idx} className="inline-block bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 px-1 py-0.5 rounded mr-1 mb-1">
                                          {addon.name || addon}
                                        </span>
                                      ))
                                    ) : (
                                      <span className="inline-block bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 px-1 py-0.5 rounded mr-1 mb-1">
                                        {typeof item.selected_addons === 'object' ? item.selected_addons.name || 'Addon' : item.selected_addons}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {/* Customization Options - Enhanced for After Project Completion and After Demo Completion */}
                              {(order.payment_option_details?.is_split_payment_order || order.payment_option === 'advance') && (
                                <div className="mt-2 space-y-1">
                                  {/* Options Information */}
                                  {item.selected_options && (
                                    <div className="text-xs">
                                      <div className="font-medium text-orange-600 dark:text-orange-400">
                                        Options: {Array.isArray(item.selected_options) ? item.selected_options.length : 1} selected
                                      </div>
                                      <div className="text-gray-600 dark:text-gray-400">
                                        {Array.isArray(item.selected_options) ? (
                                          item.selected_options.map((option, idx) => (
                                            <span key={idx} className="inline-block bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 px-1 py-0.5 rounded mr-1 mb-1">
                                              {option.name || option}
                                            </span>
                                          ))
                                        ) : (
                                          <span className="inline-block bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 px-1 py-0.5 rounded mr-1 mb-1">
                                            {typeof item.selected_options === 'object' ? item.selected_options.name || 'Option' : item.selected_options}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Colors Information */}
                                  {item.selected_colors && (
                                    <div className="text-xs">
                                      <div className="font-medium text-pink-600 dark:text-pink-400">
                                        Colors: {Array.isArray(item.selected_colors) ? item.selected_colors.length : 1} selected
                                      </div>
                                      <div className="text-gray-600 dark:text-gray-400">
                                        {Array.isArray(item.selected_colors) ? (
                                          item.selected_colors.map((color, idx) => (
                                            <span key={idx} className="inline-block bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-200 px-1 py-0.5 rounded mr-1 mb-1">
                                              {color.name || color}
                                            </span>
                                          ))
                                        ) : (
                                          <span className="inline-block bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-200 px-1 py-0.5 rounded mr-1 mb-1">
                                            {typeof item.selected_colors === 'object' ? item.selected_colors.name || 'Color' : item.selected_colors}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Layouts Information */}
                                  {item.selected_layouts && (
                                    <div className="text-xs">
                                      <div className="font-medium text-indigo-600 dark:text-indigo-400">
                                        Layouts: {Array.isArray(item.selected_layouts) ? item.selected_layouts.length : 1} selected
                                      </div>
                                      <div className="text-gray-600 dark:text-gray-400">
                                        {Array.isArray(item.selected_layouts) ? (
                                          item.selected_layouts.map((layout, idx) => (
                                            <span key={idx} className="inline-block bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 px-1 py-0.5 rounded mr-1 mb-1">
                                              {layout.name || layout}
                                            </span>
                                          ))
                                        ) : (
                                          <span className="inline-block bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 px-1 py-0.5 rounded mr-1 mb-1">
                                            {typeof item.selected_layouts === 'object' ? item.selected_layouts.name || 'Layout' : item.selected_layouts}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Custom Fields Information */}
                                  {item.custom_fields && (
                                    <div className="text-xs">
                                      <div className="font-medium text-teal-600 dark:text-teal-400">
                                        Custom Fields:
                                      </div>
                                      <div className="text-gray-600 dark:text-gray-400">
                                        {Object.entries(item.custom_fields).map(([key, value], idx) => (
                                          <div key={idx} className="inline-block bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200 px-1 py-0.5 rounded mr-1 mb-1">
                                            {key}: {value}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                          <div className="space-y-2">
                            {/* Main Product Price */}
                            <div className="flex justify-between items-center">
                              <span className="font-medium">Product:</span>
                              <div className="text-right">
                                <span className="font-semibold">
                                  {new Intl.NumberFormat('en-IN', {
                                    style: 'currency',
                                    currency: 'INR'
                                  }).format(item.price || 0)}
                                </span>
                                {/* Show original price if different */}
                                {item.selected_license === 'regular' && item.regular_license_original_price && item.regular_license_original_price !== item.price && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    <span className="line-through">
                                      {new Intl.NumberFormat('en-IN', {
                                        style: 'currency',
                                        currency: 'INR'
                                      }).format(item.regular_license_original_price)}
                                    </span>
                                    {item.regular_license_discount > 0 && (
                                      <span className="ml-2 text-green-600 dark:text-green-400">{item.regular_license_discount}% OFF</span>
                                    )}
                                  </div>
                                )}
                                {item.selected_license === 'extended' && item.extended_license_original_price && item.extended_license_original_price !== item.price && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    <span className="line-through">
                                      {new Intl.NumberFormat('en-IN', {
                                        style: 'currency',
                                        currency: 'INR'
                                      }).format(item.extended_license_original_price)}
                                    </span>
                                    {item.extended_license_discount > 0 && (
                                      <span className="ml-2 text-green-600 dark:text-green-400">{item.extended_license_discount}% OFF</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Extended Support Price */}
                            {item.extended_support && (item.support_details || item.extended_support_price) && (
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-green-600 dark:text-green-400">Extended Support:</span>
                                <div className="text-right">
                                  <span className="font-medium">
                                    {new Intl.NumberFormat('en-IN', {
                                      style: 'currency',
                                      currency: 'INR'
                                    }).format(item.support_details?.extended_support_price || item.extended_support_price || 0)}
                                  </span>
                                  {(item.support_details?.extended_support_original_price || item.extended_support_original_price) && 
                                   (item.support_details?.extended_support_original_price || item.extended_support_original_price) !== (item.support_details?.extended_support_price || item.extended_support_price) && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      <span className="line-through">
                                        {new Intl.NumberFormat('en-IN', {
                                          style: 'currency',
                                          currency: 'INR'
                                        }).format(item.support_details?.extended_support_original_price || item.extended_support_original_price)}
                                      </span>
                                      {(item.support_details?.extended_support_discount || item.extended_support_discount) > 0 && (
                                        <span className="ml-2 text-green-600 dark:text-green-400">{item.support_details?.extended_support_discount || item.extended_support_discount}% OFF</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Addons Prices */}
                            {item.selected_addons && Array.isArray(item.selected_addons) && item.selected_addons.length > 0 && (item.addon_details || item.addons) && (
                              <div className="space-y-1">
                                <span className="text-sm text-purple-600 dark:text-purple-400">Addons:</span>
                                {item.selected_addons.map((addonId, addonIndex) => {
                                  const addon = (item.addon_details || item.addons)?.find(a => a.id === addonId);
                                  return addon ? (
                                    <div key={addonIndex} className="flex justify-between items-center pl-2">
                                      <span className="text-xs text-gray-600 dark:text-gray-400">{addon.name}:</span>
                                      <div className="text-right">
                                        <span className="text-xs font-medium">
                                          {new Intl.NumberFormat('en-IN', {
                                            style: 'currency',
                                            currency: 'INR'
                                          }).format(addon.price || 0)}
                                        </span>
                                        {addon.original_price && addon.original_price !== addon.price && (
                                          <div className="text-xs text-gray-500 dark:text-gray-400">
                                            <span className="line-through">
                                              {new Intl.NumberFormat('en-IN', {
                                                style: 'currency',
                                                currency: 'INR'
                                              }).format(addon.original_price)}
                                            </span>
                                            {addon.discount_percentage > 0 && (
                                              <span className="ml-1 text-green-600 dark:text-green-400">{addon.discount_percentage}% OFF</span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ) : null;
                                })}
                              </div>
                            )}

                            {/* Customization Options Prices - Enhanced for After Project Completion and After Demo Completion */}
                            {(order.payment_option_details?.is_split_payment_order || order.payment_option === 'advance') && (
                              <>
                                {/* Options Prices */}
                                {item.selected_options && Array.isArray(item.selected_options) && item.selected_options.length > 0 && (
                                  <div className="space-y-1">
                                    <span className="text-sm text-orange-600 dark:text-orange-400">Options:</span>
                                    {item.selected_options.map((option, optionIndex) => (
                                      <div key={optionIndex} className="flex justify-between items-center pl-2">
                                        <span className="text-xs text-gray-600 dark:text-gray-400">{option.name || option}:</span>
                                        <div className="text-right">
                                          <span className="text-xs font-medium">
                                            {new Intl.NumberFormat('en-IN', {
                                              style: 'currency',
                                              currency: 'INR'
                                            }).format(option.price || 0)}
                                          </span>
                                          {option.original_price && option.original_price !== option.price && (
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                              <span className="line-through">
                                                {new Intl.NumberFormat('en-IN', {
                                                  style: 'currency',
                                                  currency: 'INR'
                                                }).format(option.original_price)}
                                              </span>
                                              {option.discount_percentage > 0 && (
                                                <span className="ml-1 text-green-600 dark:text-green-400">{option.discount_percentage}% OFF</span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Colors Prices */}
                                {item.selected_colors && Array.isArray(item.selected_colors) && item.selected_colors.length > 0 && (
                                  <div className="space-y-1">
                                    <span className="text-sm text-pink-600 dark:text-pink-400">Colors:</span>
                                    {item.selected_colors.map((color, colorIndex) => (
                                      <div key={colorIndex} className="flex justify-between items-center pl-2">
                                        <span className="text-xs text-gray-600 dark:text-gray-400">{color.name || color}:</span>
                                        <div className="text-right">
                                          <span className="text-xs font-medium">
                                            {new Intl.NumberFormat('en-IN', {
                                              style: 'currency',
                                              currency: 'INR'
                                            }).format(color.price || 0)}
                                          </span>
                                          {color.original_price && color.original_price !== color.price && (
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                              <span className="line-through">
                                                {new Intl.NumberFormat('en-IN', {
                                                  style: 'currency',
                                                  currency: 'INR'
                                                }).format(color.original_price)}
                                              </span>
                                              {color.discount_percentage > 0 && (
                                                <span className="ml-1 text-green-600 dark:text-green-400">{color.discount_percentage}% OFF</span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Layouts Prices */}
                                {item.selected_layouts && Array.isArray(item.selected_layouts) && item.selected_layouts.length > 0 && (
                                  <div className="space-y-1">
                                    <span className="text-sm text-indigo-600 dark:text-indigo-400">Layouts:</span>
                                    {item.selected_layouts.map((layout, layoutIndex) => (
                                      <div key={layoutIndex} className="flex justify-between items-center pl-2">
                                        <span className="text-xs text-gray-600 dark:text-gray-400">{layout.name || layout}:</span>
                                        <div className="text-right">
                                          <span className="text-xs font-medium">
                                            {new Intl.NumberFormat('en-IN', {
                                              style: 'currency',
                                              currency: 'INR'
                                            }).format(layout.price || 0)}
                                          </span>
                                          {layout.original_price && layout.original_price !== layout.price && (
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                              <span className="line-through">
                                                {new Intl.NumberFormat('en-IN', {
                                                  style: 'currency',
                                                  currency: 'INR'
                                                }).format(layout.original_price)}
                                              </span>
                                              {layout.discount_percentage > 0 && (
                                                <span className="ml-1 text-green-600 dark:text-green-400">{layout.discount_percentage}% OFF</span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {item.quantity || 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {(() => {
                            // Calculate total including support, addons, and customization options
                            let itemTotal = (item.price || 0) * (item.quantity || 1);
                            
                            // Add support price if extended support is selected
                            if (item.extended_support && (item.support_details?.extended_support_price || item.extended_support_price)) {
                              itemTotal += (item.support_details?.extended_support_price || item.extended_support_price || 0) * (item.quantity || 1);
                            }
                            
                            // Add addon prices if addons are selected
                            if (item.selected_addons && Array.isArray(item.selected_addons) && item.selected_addons.length > 0 && (item.addon_details || item.addons)) {
                              item.selected_addons.forEach(addonId => {
                                const addon = (item.addon_details || item.addons)?.find(a => a.id === addonId);
                                if (addon && addon.price) {
                                  itemTotal += addon.price * (item.quantity || 1);
                                }
                              });
                            }
                            
                            // Add options prices if options are selected (for After Project Completion and After Demo Completion orders)
                            if ((order.payment_option_details?.is_split_payment_order || order.payment_option === 'advance') && 
                                item.selected_options && Array.isArray(item.selected_options) && item.selected_options.length > 0) {
                              item.selected_options.forEach(option => {
                                if (option && option.price) {
                                  itemTotal += option.price * (item.quantity || 1);
                                }
                              });
                            }
                            
                            // Add colors prices if colors are selected (for After Project Completion and After Demo Completion orders)
                            if ((order.payment_option_details?.is_split_payment_order || order.payment_option === 'advance') && 
                                item.selected_colors && Array.isArray(item.selected_colors) && item.selected_colors.length > 0) {
                              item.selected_colors.forEach(color => {
                                if (color && color.price) {
                                  itemTotal += color.price * (item.quantity || 1);
                                }
                              });
                            }
                            
                            // Add layouts prices if layouts are selected (for After Project Completion and After Demo Completion orders)
                            if ((order.payment_option_details?.is_split_payment_order || order.payment_option === 'advance') && 
                                item.selected_layouts && Array.isArray(item.selected_layouts) && item.selected_layouts.length > 0) {
                              item.selected_layouts.forEach(layout => {
                                if (layout && layout.price) {
                                  itemTotal += layout.price * (item.quantity || 1);
                                }
                              });
                            }
                            
                            return new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR'
                            }).format(itemTotal);
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Order Notes */}
          {order.notes && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Order Notes</h3>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <p className="text-sm text-gray-900 dark:text-white">{order.notes}</p>
              </div>
            </div>
          )}

                                           {/* Rating and Review */}
            {(order.rating || order.review) && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                  Customer Rating & Review
                </h3>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  {order.rating && (
                    <div className="flex items-center space-x-2 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-6 h-6 ${
                            star <= order.rating
                              ? 'text-yellow-500 fill-current'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                      <span className="ml-2 text-lg font-semibold text-yellow-900 dark:text-yellow-100">
                        {order.rating}/5
                      </span>
                    </div>
                  )}
                  {order.review && (
                    <div className="mb-3">
                      <p className="text-gray-700 dark:text-gray-300 italic">
                        "{order.review}"
                      </p>
                    </div>
                  )}
                  {order.review_date && (
                    <p className="text-xs text-yellow-700 dark:text-yellow-300">
                      Reviewed on: {new Date(order.review_date).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Payment Proof */}
            {order.payment_proof && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Payment Proof</h3>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        {order.payment_proof.originalName || order.payment_proof.filename}
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        {order.payment_proof.size ? `${(order.payment_proof.size / 1024).toFixed(1)} KB` : ''} • {order.payment_proof.type}
                      </p>
                      {order.payment_proof.uploaded_at && (
                        <p className="text-xs text-blue-600 dark:text-blue-400">
                          Uploaded: {new Date(order.payment_proof.uploaded_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="text-blue-600 dark:text-blue-400">
                        <FileText className="h-8 w-8" />
                      </div>
                      <div className="flex space-x-2">
                                                 {/* View/Preview Button for Images */}
                         {order.payment_proof.type?.startsWith('image/') && (
                           <button
                             onClick={() => {
                               const imageUrl = `/api/documents/${order.payment_proof.filename}`;
                               window.open(imageUrl, '_blank');
                             }}
                             className="p-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 bg-blue-100 dark:bg-blue-900/30 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                             title="View Image"
                           >
                             <Eye className="h-4 w-4" />
                           </button>
                         )}
                         {/* View/Preview Button for PDFs */}
                         {order.payment_proof.type === 'application/pdf' && (
                           <button
                             onClick={() => {
                               const pdfUrl = `/api/documents/${order.payment_proof.filename}`;
                               window.open(pdfUrl, '_blank');
                             }}
                             className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 bg-red-100 dark:bg-red-900/30 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                             title="View PDF"
                           >
                             <Eye className="h-4 w-4" />
                           </button>
                         )}
                        {/* Download Button */}
                        <button
                          onClick={() => {
                            const downloadUrl = `/api/documents/${order.payment_proof.filename}/download`;
                            const link = document.createElement('a');
                            link.href = downloadUrl;
                            link.download = order.payment_proof.originalName || order.payment_proof.filename;
                            link.target = '_blank';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                          className="p-2 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 bg-green-100 dark:bg-green-900/30 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                          title="Download File"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        {/* Open in New Tab Button */}
                        <button
                          onClick={() => {
                            const fileUrl = `/api/documents/${order.payment_proof.filename}`;
                            window.open(fileUrl, '_blank');
                          }}
                          className="p-2 text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 bg-purple-100 dark:bg-purple-900/30 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
                          title="Open in New Tab"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Image Preview */}
                  {order.payment_proof.type?.startsWith('image/') && (
                    <div className="mt-4">
                      <img 
                        src={`/api/documents/${order.payment_proof.filename}`}
                        alt="Payment Proof"
                        className="max-w-full h-auto max-h-64 rounded-lg border border-gray-200 dark:border-gray-600"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  
                  {/* PDF Preview */}
                  {order.payment_proof.type === 'application/pdf' && (
                    <div className="mt-4">
                      <iframe
                        src={`/api/documents/${order.payment_proof.filename}#toolbar=0`}
                        className="w-full h-64 rounded-lg border border-gray-200 dark:border-gray-600"
                        title="PDF Preview"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>

        <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Edit Order Modal
const EditOrderModal = ({ order, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    order_status: order?.order_status || '',
    payment_status: order?.payment_status || '',
    notes: order?.notes || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(order.id, formData);
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Edit Order - {order?.order_number}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Order Status
            </label>
            <select
              name="order_status"
              value={formData.order_status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Payment Status
            </label>
            <select
              name="payment_status"
              value={formData.payment_status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Add order notes..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Update Order
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Delete Confirmation Modal
const DeleteConfirmModal = ({ order, onClose, onDelete }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Delete Order
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Are you sure you want to delete this order? This action cannot be undone.
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-4">
            <p className="text-sm text-gray-900 dark:text-white">
              <strong>Order:</strong> {order?.order_number}
            </p>
            <p className="text-sm text-gray-900 dark:text-white">
              <strong>Customer:</strong> {order?.customer_name}
            </p>
            <p className="text-sm text-gray-900 dark:text-white">
              <strong>Amount:</strong> {new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR'
              }).format(order?.total_amount || 0)}
            </p>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={() => onDelete(order.id)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Delete Order
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrdersManagement; 